import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faCheck,
    faXmark,
    faDiagramProject,
} from "@fortawesome/free-solid-svg-icons";
import api from "../../api/client";
import StatusBadge from "../shared/StatusBadge";
import "./QuoteView.css";

const formatCAD = (n) =>
    new Intl.NumberFormat("en-CA", {
        style: "currency",
        currency: "CAD",
    }).format(n ?? 0);

/** Client-facing read-only quote with accept / decline actions. */
const QuoteView = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();

    const [quote, setQuote] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [acceptedProject, setAcceptedProject] = useState(null);
    const [confirmDecline, setConfirmDecline] = useState(false);

    const quoteNumber = (q) => `Q-${String(q.id).padStart(4, "0")}`;

    useEffect(() => {
        api.quote(id)
            .then(({ quote: q }) => setQuote(q))
            .catch(() => setError(t("common.error")))
            .finally(() => setLoading(false));
    }, [id, t]);

    const accept = async () => {
        setBusy(true);
        setError("");
        try {
            const res = await api.acceptQuote(id);
            setQuote(res.quote);
            setAcceptedProject(res.project || null);
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    const decline = async () => {
        setBusy(true);
        setError("");
        try {
            const res = await api.declineQuote(id);
            setQuote(res.quote);
            setConfirmDecline(false);
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    if (loading)
        return (
            <p style={{ color: "var(--color-text-muted)" }}>
                {t("common.loading")}
            </p>
        );
    if (!quote)
        return (
            <p style={{ color: "var(--color-text-muted)" }}>Quote not found.</p>
        );

    const lineItems = quote.line_items || [];
    const isExpired =
        quote.expires_at && new Date(quote.expires_at) < new Date();
    const canRespond = quote.status === "sent" && !isExpired;

    return (
        <div className="quote-view">
            <div className="section-header no-print">
                <div className="quote-view__title-row">
                    <button
                        className="btn btn--ghost btn--sm"
                        onClick={() => navigate("/dashboard/quotes")}
                    >
                        ← Back
                    </button>
                    <h2>{quote.title}</h2>
                    <StatusBadge status={quote.status} />
                    <button
                        className="btn btn--ghost btn--sm"
                        onClick={() => window.print()}
                    >
                        Print / Save PDF
                    </button>
                </div>
            </div>

            {/* Printable letterhead — hidden on screen, shown when printing/saving as PDF */}
            <div className="quote-print">
                <div className="quote-print__accent-bar" />
                <div className="quote-print__header">
                    <div className="quote-print__brand">
                        <div className="quote-print__logo">KS</div>
                        <div className="quote-print__from">
                            <strong>Kyle Street</strong>
                            <span>Web Development &amp; IT Services</span>
                            <span>admin@kyle-street.com</span>
                            <span>kyle-street.com</span>
                        </div>
                    </div>
                    <div className="quote-print__doc-title">
                        <h1>Quote</h1>
                        <div className="quote-print__number">
                            {quoteNumber(quote)}
                        </div>
                        <StatusBadge status={quote.status} />
                    </div>
                </div>
                <div className="quote-print__parties">
                    <div className="quote-print__party">
                        <span className="quote-print__party-label">
                            {t("quote.client")}
                        </span>
                        <strong>{quote.client?.name}</strong>
                        {quote.client?.company && (
                            <span>{quote.client.company}</span>
                        )}
                        {quote.client?.email && (
                            <span>{quote.client.email}</span>
                        )}
                    </div>
                    <table className="quote-print__meta-table">
                        <tbody>
                            <tr>
                                <td>Title</td>
                                <td>{quote.title}</td>
                            </tr>
                            <tr>
                                <td>Issued</td>
                                <td>
                                    {new Date(
                                        quote.created_at,
                                    ).toLocaleDateString("en-CA")}
                                </td>
                            </tr>
                            {quote.expires_at && (
                                <tr>
                                    <td>{t("quote.expires")}</td>
                                    <td>
                                        {new Date(
                                            quote.expires_at,
                                        ).toLocaleDateString("en-CA")}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {error && <div className="error-message no-print">{error}</div>}

            {quote.project && !acceptedProject && (
                <p className="quote-view__project-link no-print">
                    This quote is for your project{" "}
                    <Link to={`/dashboard/projects/${quote.project.id}`}>
                        <strong>{quote.project.title}</strong>
                    </Link>
                    .
                </p>
            )}

            {acceptedProject && (
                <div className="quote-view__accepted card no-print">
                    <FontAwesomeIcon icon={faCheck} />
                    <div>
                        <strong>Quote accepted — thank you!</strong>
                        <p>
                            {quote.project_id
                                ? "The additional work has been approved and attached to your project."
                                : "A project has been created and work can begin. You can follow progress on the project page."}
                        </p>
                    </div>
                    <Link
                        to={`/dashboard/projects/${acceptedProject.id}`}
                        className="btn btn--primary btn--sm"
                    >
                        <FontAwesomeIcon icon={faDiagramProject} /> View Project
                    </Link>
                </div>
            )}

            {canRespond && !acceptedProject && (
                <div className="quote-view__actions card no-print">
                    <div>
                        <strong>This quote is awaiting your response.</strong>
                        <p>
                            Accepting will start the project. If anything looks
                            off, decline and get in touch — we can revise it.
                        </p>
                    </div>
                    <div className="quote-view__action-buttons">
                        {confirmDecline ? (
                            <>
                                <span className="quote-view__confirm-text">
                                    Decline this quote?
                                </span>
                                <button
                                    className="btn btn--danger btn--sm"
                                    onClick={decline}
                                    disabled={busy}
                                >
                                    Yes, decline
                                </button>
                                <button
                                    className="btn btn--ghost btn--sm"
                                    onClick={() => setConfirmDecline(false)}
                                >
                                    Cancel
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    className="btn btn--ghost btn--sm"
                                    onClick={() => setConfirmDecline(true)}
                                    disabled={busy}
                                >
                                    <FontAwesomeIcon icon={faXmark} /> Decline
                                </button>
                                <button
                                    className="btn btn--primary"
                                    onClick={accept}
                                    disabled={busy}
                                >
                                    <FontAwesomeIcon icon={faCheck} />{" "}
                                    {busy ? "Accepting…" : "Accept Quote"}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}

            {isExpired && quote.status === "sent" && (
                <div className="quote-view__expired card no-print">
                    This quote expired on{" "}
                    {new Date(quote.expires_at).toLocaleDateString("en-CA")}.
                    <Link to="/dashboard/support"> Contact support</Link> to
                    request an updated quote.
                </div>
            )}

            <div className="card quote-view__items">
                <table className="dash-table">
                    <thead>
                        <tr>
                            <th>Description</th>
                            <th className="dash-table__right">Qty</th>
                            <th className="dash-table__right">Unit Price</th>
                            <th className="dash-table__right">Subtotal</th>
                        </tr>
                    </thead>
                    <tbody>
                        {lineItems.map((item) => (
                            <tr key={item.id}>
                                <td>{item.description}</td>
                                <td className="dash-table__right dash-table__mono">
                                    {parseFloat(item.qty)}
                                </td>
                                <td className="dash-table__right dash-table__mono">
                                    {formatCAD(item.unit_price)}
                                </td>
                                <td className="dash-table__right dash-table__mono">
                                    {formatCAD(item.subtotal)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        {parseFloat(quote.tax_amount) > 0 && (
                            <>
                                <tr>
                                    <td
                                        colSpan={3}
                                        className="dash-table__right dash-table__muted"
                                    >
                                        Subtotal
                                    </td>
                                    <td className="dash-table__right dash-table__mono">
                                        {formatCAD(quote.subtotal)}
                                    </td>
                                </tr>
                                <tr>
                                    <td
                                        colSpan={3}
                                        className="dash-table__right dash-table__muted"
                                    >
                                        Tax
                                    </td>
                                    <td className="dash-table__right dash-table__mono">
                                        {formatCAD(quote.tax_amount)}
                                    </td>
                                </tr>
                            </>
                        )}
                        <tr className="quote-view__total-row">
                            <td colSpan={3} className="dash-table__right">
                                <strong>Total (CAD)</strong>
                            </td>
                            <td className="dash-table__right dash-table__mono">
                                <strong>{formatCAD(quote.total)}</strong>
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>

            <div className="quote-view__meta no-print">
                {quote.expires_at && !isExpired && (
                    <span>
                        Valid until{" "}
                        {new Date(quote.expires_at).toLocaleDateString("en-CA")}
                    </span>
                )}
                <span>
                    Issued{" "}
                    {new Date(quote.created_at).toLocaleDateString("en-CA")}
                </span>
            </div>

            {quote.notes && (
                <div className="card quote-view__notes">
                    <h3>Notes</h3>
                    <p>{quote.notes}</p>
                </div>
            )}

            <div className="quote-print__footer">
                <p className="quote-print__thanks">
                    Thank you for the opportunity to work together.
                </p>
                <p>
                    This quote is valid until the date above. Accept or decline
                    it from your client portal, or reply with any questions.
                </p>
            </div>
        </div>
    );
};

export default QuoteView;
